"use client";

import React from "react";

import { useAtom, useSetAtom } from "jotai";
import { setCookie } from "cookies-next/client";
import { useAction } from "next-safe-action/hooks";
import { useForm, FormProvider, useWatch } from "react-hook-form";

import AuthSceneLogin from "./scene/login";
import AuthScenePhone from "./scene/phone";
import AuthSceneRegister from "./scene/register";
import AuthSceneUserGoal from "./scene/goal";

import { toast } from "@/components/ui/sonner";

import { loginRegisterUser, sendOtpToUser } from "@/fetchers/auth";

import {
  authActionAtom,
  authScreenAtom,
  isAuthenticatedAtom,
  showLoginAtom,
} from "@/store/auth";

import useMoengageEventTracker from "@/hooks/use-moengage";
import EVENT_NAME from "@/hooks/use-moengage/event-name.json";
import EVENT_ATTRIBUTE from "@/hooks/use-moengage/event-attribute.json";

import { cn } from "@/lib/utils";
import { defaultCookieOptions } from "@/lib/cookie-options";

function Auth() {
  const form = useForm();
  const trackMoengageEvent = useMoengageEventTracker();

  const [showLogin, setShowLogin] = useAtom(showLoginAtom);
  const [authScreen, setAuthScreen] = useAtom(authScreenAtom);

  const setIsAuthenticated = useSetAtom(isAuthenticatedAtom);
  const setAuthAction = useSetAtom(authActionAtom);

  const [loginScreen, setLoginScreen] = React.useState("PHONE"); // PHONE | LOGIN | SIGNUP

  const loginOtp = useWatch({ name: "phoneOtp", control: form.control });

  const handleActionError = ({ error }) => {
    toast({
      variant: "error",
      title: error.serverError || "Something went wrong!",
    });
  };

  const { executeAsync: sendOtp } = useAction(sendOtpToUser, {
    onError: handleActionError,
  });
  const { executeAsync: loginUser, isExecuting: isSubmittingLogin } = useAction(
    loginRegisterUser,
    {
      onError: handleActionError,
    }
  );

  const onSubmit = React.useCallback(async () => {
    if (authScreen !== "AUTHENTICATION") return;

    const values = form.getValues();

    if (loginScreen === "PHONE") {
      const { data: sendOtpData } = await sendOtp({
        mobileNumber: values.phone,
        mode: 1,
      });

      if (sendOtpData) {
        setLoginScreen(sendOtpData.action === 1 ? "LOGIN" : "SIGNUP");
      }
    } else {
      const { data: loginData } = await loginUser({
        name: values.name,
        email: values.email,
        mobileNumber: values.phone,
        otp: values.phoneOtp,
        action: loginScreen === "LOGIN" ? 1 : 2,
      });

      // Reset OTP field value after successful submission
      form.resetField("phoneOtp", { defaultValue: "" });

      if (loginData) {
        setIsAuthenticated(true);
        setAuthAction(loginData.action);

        setCookie("userID", loginData.userId, defaultCookieOptions);
        setCookie("apiToken", loginData.apiToken, defaultCookieOptions);
        setCookie("deviceID", loginData.deviceId, defaultCookieOptions);
        setCookie("mobileNumber", loginData.mobileNumber, defaultCookieOptions);

        const userAttrs = {
          user_id: loginData.userId,
          user_type: loginData.userType,
          user_email: loginData.email,
          user_mobile: loginData.mobileNumber,
          expired_count: loginData.expiredCount,
          user_name: loginData.firstName,
          active_count: loginData.activeCount,
          is_paid_user: loginData.paidStatus ? "yes" : "no",
          user_state: loginData.state,
          user_city: loginData.city,
          user_type: "registered",
        };

        if (loginData.action === 2) {
          setAuthScreen("EXAM_PREF");

          // Track signup event
          trackMoengageEvent(EVENT_NAME.SIGNUP, {
            [EVENT_ATTRIBUTE.SIGNUP_VIA]: "otp",
            ...userAttrs,
          });
        } else {
          setShowLogin(false);
          setAuthScreen("AUTHENTICATION");

          // Track login event
          trackMoengageEvent(EVENT_NAME.LOGIN_BTN_TAPPED, {
            [EVENT_ATTRIBUTE.LOGIN_STATUS]: "success",
            [EVENT_ATTRIBUTE.LOGIN_VIA]: "otp",
            ...userAttrs,
          });
        }
      }
    }
  }, [
    sendOtp,
    loginUser,
    authScreen,
    loginScreen,
    form,
    setAuthAction,
    setAuthScreen,
    setIsAuthenticated,
    setShowLogin,
    trackMoengageEvent,
  ]);

  React.useEffect(() => {
    if (loginOtp?.length !== 6 || loginScreen !== "LOGIN" || isSubmittingLogin)
      return;

    onSubmit();
  }, [isSubmittingLogin, loginOtp, loginScreen, onSubmit]);

  const handleSendOtp = (mode) => () => {
    const { phone: mobileNumber } = form.getValues();

    sendOtp({ mobileNumber, mode }).then(({ data }) => {
      if (data) {
        setLoginScreen(data.action === 1 ? "LOGIN" : "SIGNUP");
      }
    });
  };

  const handleOnBackClick = (mode) => {
    const values = form.getValues();
    if (mode === "EDIT_PHONE") {
      setLoginScreen("PHONE");

      trackMoengageEvent(EVENT_NAME.EDIT_PHONE_NUMBER_CLICKED, {
        [EVENT_ATTRIBUTE.PREVIOUS_MOBILE_NUMBER]: values.phone || "",
        [EVENT_ATTRIBUTE.SCREEN_NAME]: loginScreen.toLowerCase(),
      });
    } else if (loginScreen === "PHONE") {
      setShowLogin(false);
      setAuthScreen("AUTHENTICATION");
      setLoginScreen("PHONE");

      trackMoengageEvent(EVENT_NAME.LOGIN_DISMISSED, {
        [EVENT_ATTRIBUTE.DROP_POINT]: "phone",
        [EVENT_ATTRIBUTE.USER_MOBILE]: values.phone || "",
      });
    } else if (loginScreen === "LOGIN") {
      setLoginScreen("PHONE");

      trackMoengageEvent(EVENT_NAME.LOGIN_DISMISSED, {
        [EVENT_ATTRIBUTE.DROP_POINT]: "otp",
        [EVENT_ATTRIBUTE.USER_MOBILE]: values.phone,
      });
    } else if (loginScreen === "SIGNUP") {
      setLoginScreen("PHONE");

      trackMoengageEvent(EVENT_NAME.LOGIN_DISMISSED, {
        [EVENT_ATTRIBUTE.DROP_POINT]: "signup",
        [EVENT_ATTRIBUTE.NAME]: values.name,
        [EVENT_ATTRIBUTE.USER_MOBILE]: values.phone,
        [EVENT_ATTRIBUTE.USER_EMAIL]: values.email,
      });
    }
  };

  const handleExamPrefClose = () => {
    setShowLogin(false);
    setAuthScreen("AUTHENTICATION");
    setLoginScreen("PHONE");

    trackMoengageEvent(EVENT_NAME.LOGIN_DISMISSED, {
      [EVENT_ATTRIBUTE.DROP_POINT]: "exam_preference",
    });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 bg-black/50 z-70 transition-opacity duration-300 ease-in-out",
          showLogin ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setShowLogin(false)}
      />

      {/* Login Panel */}
      <aside
        className={cn(
          "fixed bg-primary-foreground right-0 top-0 bottom-0 z-80 w-full max-w-119.5 drop-shadow-2xl",
          "translate-x-full transition-transform duration-300 ease-in-out p-[1.5625rem_2.5rem_1.875rem]",
          showLogin && "translate-x-0"
        )}
      >
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="h-full">
            {authScreen === "AUTHENTICATION" && (
              <>
                {loginScreen === "PHONE" && (
                  <AuthScenePhone onClose={handleOnBackClick} />
                )}
                {loginScreen === "LOGIN" && (
                  <AuthSceneLogin
                    onSendOtpOnEmail={handleSendOtp(2)}
                    onResendOtp={handleSendOtp(1)}
                    onBack={handleOnBackClick}
                  />
                )}
                {loginScreen === "SIGNUP" && (
                  <AuthSceneRegister
                    onResendOtp={handleSendOtp(1)}
                    onBack={handleOnBackClick}
                  />
                )}
              </>
            )}

            {authScreen === "EXAM_PREF" && (
              <AuthSceneUserGoal onClose={handleExamPrefClose} />
            )}
          </form>
        </FormProvider>
      </aside>
    </>
  );
}

export default Auth;
